import React, { useEffect, useState } from 'react';
import { X, AlertTriangle, Trash2, Loader, RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import { api } from '../lib/api';
import toast from 'react-hot-toast';

type Baza = { id: string; name: string };

type Props = {
  baza: Baza;
  onClose: () => void;
  onDeleted: (id: string) => void;
};

export function IzbrisBazeDialog({ baza, onClose, onDeleted }: Props) {
  const [koda, setKoda] = useState('');
  const [vnos, setVnos] = useState('');
  const [nalagam, setNalagam] = useState(true);
  const [brisem, setBrisem] = useState(false);

  const pridobiKodo = async () => {
    setNalagam(true);
    setVnos('');
    try {
      const r = await api.post(`/companies/${baza.id}/priprava-izbrisa`);
      setKoda(r.data.koda);
    } catch {
      toast.error('Kode ni bilo mogoče pridobiti.');
      onClose();
    } finally {
      setNalagam(false);
    }
  };

  useEffect(() => {
    pridobiKodo();
  }, [baza.id]);

  const ujema = koda !== '' && vnos.trim() === koda;

  const izbrisi = async () => {
    if (!ujema || brisem) return;
    setBrisem(true);
    try {
      await api.delete(`/companies/${baza.id}`, { data: { koda: vnos.trim() } });
      toast.success(`Baza "${baza.name}" je izbrisana.`);
      onDeleted(baza.id);
      onClose();
    } catch (e: any) {
      const det = e?.response?.data?.detail || 'Brisanje ni uspelo.';
      toast.error(det);
      if (String(det).toLowerCase().includes('potek')) {
        pridobiKodo(); // koda potekla -> generiraj novo
      }
    } finally {
      setBrisem(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-red-200 dark:border-red-900 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* glava */}
        <div className="flex items-center gap-3 px-5 py-4 bg-red-50 dark:bg-red-950/40 border-b border-red-200 dark:border-red-900">
          <div className="w-9 h-9 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-red-700 dark:text-red-300">Izbriši bazo</div>
            <div className="text-sm text-gray-600 dark:text-gray-400 truncate">{baza.name}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* telo */}
        <div className="px-5 py-4 space-y-4">
          <p className="text-sm text-red-700 dark:text-red-300 font-medium leading-relaxed">
            Pozor: brisanje je <span className="underline">nepovratno</span>. Vse podatke te baze
            (dokumente, partnerje, plačila, dnevnik) boš izgubil <span className="font-bold">takoj</span> in
            brez možnosti povrnitve.
          </p>

          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
              Za potrditev natančno prepiši to kodo:
            </div>
            <div className="flex items-center gap-2">
              {nalagam ? (
                <div className="flex items-center gap-2 text-gray-400 text-sm py-2">
                  <Loader className="w-4 h-4 animate-spin" /> pridobivam kodo…
                </div>
              ) : (
                <code className="select-none text-lg font-bold tracking-[0.2em] text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 px-3 py-2 rounded-lg border border-red-200 dark:border-red-900">
                  {koda}
                </code>
              )}
              <button
                onClick={pridobiKodo}
                disabled={nalagam}
                title="Osveži kodo"
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-40"
              >
                <RefreshCw className={clsx('w-4 h-4', nalagam && 'animate-spin')} />
              </button>
            </div>
          </div>

          <input
            value={vnos}
            onChange={(e) => setVnos(e.target.value)}
            placeholder="Prepiši kodo tukaj"
            autoFocus
            spellCheck={false}
            autoComplete="off"
            className={clsx(
              'w-full px-3 py-2 rounded-lg border bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono tracking-[0.2em] outline-none transition',
              vnos === ''
                ? 'border-gray-300 dark:border-gray-700'
                : ujema
                ? 'border-green-400 focus:border-green-500'
                : 'border-red-300 focus:border-red-500'
            )}
          />
        </div>

        {/* noga */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-800">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            Prekliči
          </button>
          <button
            onClick={izbrisi}
            disabled={!ujema || brisem}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-2 transition',
              !ujema || brisem
                ? 'bg-red-300 dark:bg-red-900/50 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700'
            )}
          >
            {brisem ? <Loader className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Dokončno izbriši
          </button>
        </div>
      </div>
    </div>
  );
}

export default IzbrisBazeDialog;
